import { defineComponent as _defineComponent } from 'vue'
import { unref as _unref, resolveComponent as _resolveComponent, createVNode as _createVNode, openBlock as _openBlock, createBlock as _createBlock, createCommentVNode as _createCommentVNode, withCtx as _withCtx, Fragment as _Fragment, createElementBlock as _createElementBlock } from "vue"

import { ConfigProvider } from "ant-design-vue";
import zhCN from "ant-design-vue/es/locale/zh_CN";
import dayjs from "dayjs";
import "dayjs/locale/zh-cn";
import { t } from "@/plugins/i18n";

import {
  ref,
  onMounted,
  defineAsyncComponent,
  computed,
  watch,
  onUnmounted,
} from "vue";
import { Modal, message } from "ant-design-vue";
import { provide } from "vue";
import {
  useChatControls,
  chatControlsKey,
} from "@/components/FloatingCustomerService/useChatControls";
import { useRoute, useRouter } from "vue-router";
import { useStore } from "vuex";
import { useUpdate } from "@/hooks/useUpdate";
import { useNotificationPermission } from "@/hooks/useNotificationPermission";
import { useUnreadMessage } from "@/hooks/useUnreadMessages";
import { DownloadTracker } from "@/utils/downloadTracker";
import { useAuth } from "@/utils/useAuth";
import { teardownChatwoot } from "@/utils/chatwoot";


export default /*@__PURE__*/_defineComponent({
  __name: 'App',
  setup(__props) {

const { checkUpdate } = useUpdate();
const { showNotificationDialog } = useNotificationPermission();
const route = useRoute();
const router = useRouter();
const routeReady = ref(false);

const chatControls = useChatControls();
provide(chatControlsKey, chatControls);
const store = useStore();

// 添加token刷新逻辑
const { refreshTokenIfNeeded } = useAuth(chatControls);

dayjs.locale(zhCN.locale);

const locale = zhCN;

const UpdateVersionRef = ref();
const isShow2 = ref(false);
const IS_ELECTRON = process.env.IS_ELECTRON;
const hasElectronRuntime = computed(
  () => IS_ELECTRON && typeof window !== "undefined" && !!window.electronAPI
);
const FloatingCustomerService = defineAsyncComponent(
  () => import("@/components/FloatingCustomerService/index.vue")
);
const UpdateVersionDialog = defineAsyncComponent(
  () => import("@/components/UpdateVersionDialog.vue")
);

const checkClientRef = ref();
const isShow = ref(false);
const CheckClientVersion = defineAsyncComponent(
  () => import("@/components/CheckClientVersion.vue")
);
const FloatToolbar = defineAsyncComponent(
  () => import("@/components/FloatToolbar/index.vue")
);
const isLogin = computed(() => store.state.user.isLogin);
const isFloatingServiceHidden = computed(() =>
  route.matched.some((record) => record.meta.hideFloatingService)
);
const shouldShowService = computed(() => {
  return (
    routeReady.value && !isFloatingServiceHidden.value && isLogin.value === "1"
  );
});
const shouldShowToolBar = computed(() => {
  return routeReady.value && !isFloatingServiceHidden.value && !IS_ELECTRON;
});
const shouldHideFloatingService = computed(
  () => routeReady.value && isFloatingServiceHidden.value
);
const { startPolling, stopPolling, isPolling } = useUnreadMessage();

// 监听登录状态变化，登录成功后检查更新
watch(
  () => isLogin.value,
  (newValue, oldValue) => {
    if (newValue === "1" && oldValue === "0") {
      // 用户刚登录成功，检查更新
      checkUpdate();
    }
  }
);

router.isReady().then(() => {
  routeReady.value = true;
});

hasElectronRuntime.value &&
  onMounted(async () => {
    const electronAPI = window.electronAPI;
    if (!electronAPI) return;
    // 启动时检查一次token
    const isLogin = localStorage.getItem("isLogin") === "1";
    if (isLogin) {
      const refreshSuccess = await refreshTokenIfNeeded();
      if (!refreshSuccess) {
        store.dispatch("user/logout");
        return;
      }
      // 用户已登录，检查更新
      checkUpdate();
    }

    const PLATFORM = electronAPI.platform;
    if (PLATFORM === "linux") {
      isShow.value = true;
      const timer = setTimeout(() => {
        checkClientRef.value?.checkUpdate(false);
        clearTimeout(timer);
      }, 2000);
    } else {
      showNotificationDialog();
    }
    electronAPI.openNewVersionDialog(() => {
      Modal.confirm({
        title: t("update.checkUpdate"),
        content: t("update.updateContent"),
        onOk() {
          electronAPI.vueSend("ok");
        },
        onCancel() {
          electronAPI.vueSend("cancel");
        },
        okText: t("update.update"),
        cancelText: t("update.cancel"),
        class: "test",
      });
    });
    electronAPI.openDownloadFinishDialog(() => {
      isShow2.value = false;
      Modal.confirm({
        title: t("update.updateComplete"),
        content: t("update.updateCompleteContent"),
        onOk() {
          electronAPI.vueSend("install");
        },
        onCancel() {
          electronAPI.vueSend("cancel");
        },
        okText: t("update.install"),
        cancelText: t("update.cancel"),
        class: "test",
      });
    });
    electronAPI.openProgressDialog(() => {
      console.log("openProgressDialog");
      isShow2.value = true;
      if (UpdateVersionRef.value) {
        UpdateVersionRef.value.showModal();
      } else {
        const timer = setTimeout(() => {
          UpdateVersionRef.value?.showModal();
          clearTimeout(timer);
        }, 500);
      }
    });
    electronAPI.changeProgressPercent((data: unknown) => {
      UpdateVersionRef.value?.changePercent(data);
    });
    electronAPI.showSuccessAlert((data: string) => {
      message.success(data);
    });
    electronAPI.clearLocalStorage(() => {
      localStorage.clear();
    });
    electronAPI.showMessage((data: string) => {
      message.info(data);
    });
    electronAPI.sendTrackEvent(
      (option: { datasetName: string; type: string }) => {
        try {
          if (window._paq) {
            const cleanDatasetName = option.datasetName.replace(
              /\.[^/.]+$/,
              ""
            );
            window._paq.push([
              "trackEvent",
              option.type,
              cleanDatasetName,
              cleanDatasetName,
              1,
            ]);
          }
        } catch (e) {
          console.log(e);
        }
      }
    );

    electronAPI.sendTrackEventSpeed(
      (option: {
        url: string;
        name: string;
        speed: number;
        type?: string;
        urlSpeeds?: Record<string, number>;
      }) => {
        try {
          let userInfoObj = JSON.parse(
            localStorage.getItem("userInfo") || "{}"
          );
          if (option.type === "new") {
            userInfoObj.id &&
              DownloadTracker.startNewDownload(
                userInfoObj.id + "-" + option.url
              );
            return;
          }
          if (userInfoObj.id && window._paq) {
            // 如果传入了分 URL 速度，则逐个上报；否则沿用总速度 + 单一 URL
            const urlEntries =
              option.urlSpeeds && typeof option.urlSpeeds === "object"
                ? Object.entries(option.urlSpeeds)
                : [];
            const targets =
              urlEntries.length > 0
                ? urlEntries.map(([u, s]) => ({ url: u, speed: s }))
                : [{ url: option.url, speed: option.speed }];

            targets.forEach(({ url, speed }) => {
              const downloadCount = DownloadTracker.getCurrentCount(
                userInfoObj.id + "-" + url
              );
              const trackId = `${userInfoObj.id}-${downloadCount}-${url}`;
              window._paq.push([
                "trackEvent",
                "DownloadSpeed",
                trackId,
                option.name,
                (speed / (1024 * 1024)).toFixed(2),
              ]);
            });
          }
        } catch (e) {
          console.error("[Matomo] sendTrackEventSpeed 处理出错", e);
        }
      }
    );
    try {
      let userInfoObj = JSON.parse(localStorage.getItem("userInfo") || "{}");
      if (userInfoObj.id && window._paq) {
        window._paq.push([
          "setUserId",
          userInfoObj.userName + "（" + userInfoObj.id + "）",
        ]);
        window._paq.push(["trackPageView"]);
      }
    } catch (error) {
      console.error("parse userInfo error:", error);
    }
    // Track crash events
    electronAPI.openCrashTracker(
      (event: unknown, data: { type: string; details: string }) => {
        if (window._paq) {
          window._paq.push([
            "trackEvent",
            "AppCrash",
            data.type,
            data.details,
            1,
          ]);
        }
      }
    );

    // Track restart events
    electronAPI.openRestartTracker(
      (event: unknown, data: { reason: string }) => {
        if (window._paq) {
          window._paq.push(["trackEvent", "AppRestart", data.reason, "", 1]);
        }
      }
    );

    // Track relaunch events
    electronAPI.openRelaunchTracker(
      (event: unknown, data: { type: string; details: string }) => {
        if (window._paq) {
          window._paq.push([
            "trackEvent",
            "AppRelaunch",
            data.type,
            data.details,
            1,
          ]);
        }
      }
    );
  });
const shouldPoll = computed(() => {
  const token = localStorage.getItem("token");
  const v = isLogin.value;
  const isLoggedIn = v === "1" || v === 1 || v === true || v === "true";
  return hasElectronRuntime.value && isLoggedIn && !!token;
});
!IS_ELECTRON &&
  onMounted(() => {
    try {
      let userInfoObj = JSON.parse(localStorage.getItem("userInfo") || "{}");
      if (userInfoObj.id && window._paq) {
        window._paq.push([
          "setUserId",
          userInfoObj.userName + "（" + userInfoObj.id + "）",
        ]);
        window._paq.push(["trackPageView"]);
      }
    } catch (error) {
      console.error("parse userInfo error:", error);
    }
  });
// 监听 shouldPoll
watch(
  () => shouldHideFloatingService.value,
  (shouldHide) => {
    if (shouldHide) {
      teardownChatwoot();
    }
  },
  { immediate: true }
);

watch(
  () => shouldPoll.value,
  (newValue) => {
    if (newValue) {
      startPolling();
    } else {
      stopPolling();
    }
  },
  { immediate: true }
);
onUnmounted(() => {
  if (isPolling.value) {
    stopPolling();
  }
});

return (_ctx: any,_cache: any) => {
  const _component_router_view = _resolveComponent("router-view")!

  return (_openBlock(), _createElementBlock(_Fragment, null, [
    _createVNode(_unref(ConfigProvider), {
      locale: _unref(locale),
      "auto-insert-space-in-button": false,
      theme: {
      token: {
        colorPrimary: '#4749ad',
        colorInfo: '#4749ad',
        borderRadius: 2,
        borderRadiusSM: 2,
      },
    }
    }, {
      default: _withCtx(() => [
        _createVNode(_component_router_view),
        (shouldShowService.value)
          ? (_openBlock(), _createBlock(_unref(FloatingCustomerService), { key: 0 }))
          : _createCommentVNode("", true)
      ]),
      _: 1
    }, 8, ["locale"]),
    (isShow2.value && _unref(IS_ELECTRON))
      ? (_openBlock(), _createBlock(_unref(UpdateVersionDialog), {
          key: 0,
          ref_key: "UpdateVersionRef",
          ref: UpdateVersionRef
        }, null, 512))
      : _createCommentVNode("", true),
    (isShow.value && _unref(IS_ELECTRON))
      ? (_openBlock(), _createBlock(_unref(CheckClientVersion), {
          key: 1,
          ref_key: "checkClientRef",
          ref: checkClientRef
        }, null, 512))
      : _createCommentVNode("", true),
    (shouldShowToolBar.value)
      ? (_openBlock(), _createBlock(_unref(FloatToolbar), { key: 2 }))
      : _createCommentVNode("", true)
  ], 64))
}
}

})